import type { Difficulty, RegionScope } from "../types.ts";

const STORAGE_KEY = "earth-puzzle:v1:lang";

export type Lang = "zh" | "en";

type Dict = Record<string, string>;

const ZH: Dict = {
  appTitle: "拼回世界",
  eyebrow: "世界地理拼圖",
  tagline: "把打散的國家拼回地球上正確的位置，用時越短、失誤越少，分數越高。",
  statCountries: "國家",
  statPieces: "拼圖片",
  recordsSub: "查看各範圍的個人最佳",
  regionGroup: "範圍",
  difficultyGroup: "難度（決定不連續地塊要拆多細）",
  start: "開始遊戲",
  loading: "載入中…",
  retry: "重試",
  records: "我的紀錄",
  noData: "此組合沒有可用資料",
  pieceCount: "{countries} 國 ／ {pieces} 片",
  noRecord: "尚無紀錄",
  bestLine: "最佳 {score} 分（{time} {stars}）",
  loadFailed: "地圖資料載入失敗，請檢查網路後重試（{file}）",
  noRecordsYet: "還沒有任何紀錄，先玩一局吧",
  recordsCorrupt: "本地紀錄損毀，無法顯示",

  statScope: "範圍",
  statTimer: "用時",
  statProgress: "進度",
  statMistakes: "錯誤",
  statBest: "最佳",
  hint: "提示 ({n})",
  pause: "暫停",
  resume: "繼續",
  quit: "返回選單",
  paused: "已暫停",

  complete: "完成！",
  resultTime: "{percent}%　用時 {time}",
  resultStats: "拼圖片 {pieces}　錯誤 {mistakes}　提示 {hints}",
  newRecord: "🎉 新紀錄！",
  previousBest: "先前最佳 {score}（{time}）",
  playAgain: "再玩一次",
  storageWriteFailed: "本地紀錄儲存失敗，下次進入將無法比較",
  storageReset: "本地紀錄格式損毀，已重置",
  storageUnavailable: "此瀏覽器無法儲存本地紀錄",

  lightsOn: "開燈",
  lightsOff: "關燈",
  langSwitch: "EN",

  "region.World": "全世界",
  "region.Africa": "非洲",
  "region.Asia": "亞洲",
  "region.Europe": "歐洲",
  "region.NorthAmerica": "北美洲",
  "region.SouthAmerica": "南美洲",
  "region.Oceania": "大洋洲",

  "difficulty.Easy": "簡單",
  "difficulty.Medium": "普通",
  "difficulty.Hard": "困難",
  "difficulty.VeryHard": "極難",
  "difficultyHint.Easy": "一國一片，離島跟著母國",
  "difficultyHint.Medium": "大島單獨成片",
  "difficultyHint.Hard": "中型島嶼也分開",
  "difficultyHint.VeryHard": "所有島嶼全部分開",
};

const EN: Dict = {
  appTitle: "Piece the World",
  eyebrow: "World Geography Puzzle",
  tagline: "Drag each country back to where it belongs. Faster and fewer mistakes means a higher score.",
  statCountries: "Countries",
  statPieces: "Pieces",
  recordsSub: "Personal bests by region",
  regionGroup: "Region",
  difficultyGroup: "Difficulty (how finely landmasses are split)",
  start: "Start",
  loading: "Loading…",
  retry: "Retry",
  records: "My records",
  noData: "No data for this combination",
  pieceCount: "{countries} countries ／ {pieces} pieces",
  noRecord: "No record yet",
  bestLine: "Best {score} ({time} {stars})",
  loadFailed: "Could not load map data. Check your connection and retry ({file})",
  noRecordsYet: "No records yet — play a round first",
  recordsCorrupt: "Local records are corrupted and cannot be shown",

  statScope: "Region",
  statTimer: "Time",
  statProgress: "Progress",
  statMistakes: "Misses",
  statBest: "Best",
  hint: "Hint ({n})",
  pause: "Pause",
  resume: "Resume",
  quit: "Main menu",
  paused: "Paused",

  complete: "Complete!",
  resultTime: "{percent}%　Time {time}",
  resultStats: "Pieces {pieces}　Misses {mistakes}　Hints {hints}",
  newRecord: "🎉 New record!",
  previousBest: "Previous best {score} ({time})",
  playAgain: "Play again",
  storageWriteFailed: "Could not save your record; it will not be available next time",
  storageReset: "Local records were corrupted and have been reset",
  storageUnavailable: "This browser cannot store local records",

  lightsOn: "Lights on",
  lightsOff: "Lights off",
  langSwitch: "中文",

  "region.World": "World",
  "region.Africa": "Africa",
  "region.Asia": "Asia",
  "region.Europe": "Europe",
  "region.NorthAmerica": "North America",
  "region.SouthAmerica": "South America",
  "region.Oceania": "Oceania",

  "difficulty.Easy": "Easy",
  "difficulty.Medium": "Medium",
  "difficulty.Hard": "Hard",
  "difficulty.VeryHard": "Very hard",
  "difficultyHint.Easy": "One piece per country, islands included",
  "difficultyHint.Medium": "Large islands split off",
  "difficultyHint.Hard": "Medium islands split too",
  "difficultyHint.VeryHard": "Every island separate",
};

const DICTS: Record<Lang, Dict> = { zh: ZH, en: EN };

let current: Lang = "zh";

export function t(key: string, vars?: Record<string, string | number>): string {
  const raw = DICTS[current][key] ?? DICTS.zh[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => String(vars[name] ?? `{${name}}`));
}

export const lang = (): Lang => current;
export const regionLabel = (r: RegionScope): string => t(`region.${r}`);
export const difficultyLabel = (d: Difficulty): string => t(`difficulty.${d}`);
export const difficultyHint = (d: Difficulty): string => t(`difficultyHint.${d}`);

export const mmss = (seconds: number): string =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(Math.floor(seconds) % 60).padStart(2, "0")}`;

export const stars = (n: number): string => "★".repeat(n) + "☆".repeat(3 - n);

function stored(): Lang | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "zh" || value === "en" ? value : null;
  } catch {
    return null;
  }
}

export class LangToggle {
  private readonly buttons: HTMLButtonElement[];
  private readonly onChange: () => void;

  constructor(buttons: HTMLButtonElement[], onChange: () => void, signal?: AbortSignal) {
    this.buttons = buttons;
    this.onChange = onChange;
    current = stored() ?? "en"; // 預設英文，不看瀏覽器語言
    this.apply();
    for (const button of buttons) {
      button.addEventListener("click", () => this.toggle(), { signal });
    }
  }

  private apply(): void {
    document.documentElement.lang = current === "zh" ? "zh-Hant" : "en";
    document.title = t("appTitle");
    // 靜態文字統一由 data-i18n 驅動，動態產生的字串則在各自的 render 中呼叫 t()
    for (const node of document.querySelectorAll<HTMLElement>("[data-i18n]")) {
      node.textContent = t(node.dataset.i18n!);
    }
    for (const button of this.buttons) {
      const text = button.querySelector<HTMLElement>(".label");
      if (text) text.textContent = t("langSwitch");
      else button.textContent = t("langSwitch");
    }
  }

  toggle(): void {
    current = current === "zh" ? "en" : "zh";
    try {
      localStorage.setItem(STORAGE_KEY, current);
    } catch {
      // 存不了就只在本次瀏覽生效
    }
    this.apply();
    this.onChange();
  }
}
